import { css } from '@emotion/core'
import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { Button, ResponsiveTableRow, Separator, H5, DangerButton } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import { PersonLink, RelationshipLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DotSeparator } from '../../metadata/MetaData'
import PersonInfo from './PersonInfo'
import OwnerId from '../../common/OwnerId'
import AddRelationships from './AddRelationships'

const floatRight = css`
  float: right;
`

export default function ParentsPanel({
  handleRemoveSelected,
  handleRestoreSelected,
  currentPerson,
  onRemove,
  onRestore,
  accessControls,
  tombstonedParentsRelationships,
  showTombstonedRelationships,
}) {
  const { isAuthorized } = useContext(PermissionsContext)
  const { t } = useTranslation()
  const parentsRelationshipsCount = currentPerson.relationships.parents ? currentPerson.relationships.parents.length : 0
  const tombstonedParentsRelationshipsCount = tombstonedParentsRelationships ? tombstonedParentsRelationships.length : 0
  const [relationshipsToRemove, setRelationshipsToRemove] = useState([])
  const [relationshipsToRestore, setRelationshipsToRestore] = useState([])

  const showSeparator = () => {
    if (parentsRelationshipsCount > 0) {
      return true
    }
    if (tombstonedParentsRelationshipsCount > 0 && showTombstonedRelationships) {
      return true
    }
    return false
  }

  return (
    <div>
      <H5 growX>
        {t('person.relationships.parentsof_plural', 'Parents ({count}) of', {
          count: parentsRelationshipsCount + (showTombstonedRelationships ? tombstonedParentsRelationshipsCount : 0),
        })}
        {': '}
        <PersonLink personId={currentPerson.id} personName={currentPerson.name} />
        <DotSeparator />
        {currentPerson.id}
      </H5>
      {isAuthorized('FtWritePermission') && parentsRelationshipsCount > 0 && relationshipsToRemove.length > 0 && (
        <DangerButton
          id="parentCollectiveRemove"
          Icon={ContentDelete}
          onClick={() => handleRemoveSelected(relationshipsToRemove, 'parentchild')}
        >
          {t('action.removeParents', 'Remove ({count}) Parents', {
            count: relationshipsToRemove.length,
          })}
        </DangerButton>
      )}
      {parentsRelationshipsCount > 0 && (
        <DmcResponsiveCheckboxTable
          id="parentsTable"
          totalColumnCount={6}
          onSelectionChange={setRelationshipsToRemove}
          atSizeColumns={{
            default: {
              columnWidths: [2, 2, 2, 2, 2, 2],
              labels: [
                t('parent1', 'Parent 1'),
                t('owner', 'Owner'),
                t('parent2', 'Parent 2'),
                t('owner', 'Owner'),
                t('person.relationships.relationshipid', 'Relationship ID'),
                '',
              ],
            },
          }}
        >
          {currentPerson.relationships.parents &&
            currentPerson.relationships.parents.map((relationship) => (
              <ResponsiveTableRow
                id={`parent_${relationship.relationshipId}`}
                key={relationship.relationshipId}
                name={relationship.relationshipId}
                column1={
                  <PersonInfo
                    key={`${relationship.relationshipId}_parent1`}
                    person={relationship.parent1}
                    accessControls={accessControls}
                  />
                }
                column2={
                  <OwnerId
                    key={`${relationship.relationshipId}_parent1_ownerId`}
                    ownerInfo={relationship.parent1?.ownerInfo}
                  />
                }
                column3={
                  <PersonInfo
                    key={`${relationship.relationshipId}_parent2`}
                    person={relationship.parent2}
                    accessControls={accessControls}
                  />
                }
                column4={
                  <OwnerId
                    key={`${relationship.relationshipId}_parent2_ownerId`}
                    ownerInfo={relationship.parent2?.ownerInfo}
                  />
                }
                column5={
                  <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="parentchild" />
                }
                column6={
                  <span css={floatRight}>
                    <DangerButton
                      id={`parentRemoveBtn_${relationship.relationshipId}`}
                      Icon={ContentDelete}
                      onClick={() => onRemove(relationship.relationshipId, 'parentchild')}
                      disabled={!isAuthorized('FtWritePermission')}
                    >
                      {t('action.remove', 'Remove')}
                    </DangerButton>
                  </span>
                }
              />
            ))}
        </DmcResponsiveCheckboxTable>
      )}
      {showTombstonedRelationships && tombstonedParentsRelationships && tombstonedParentsRelationshipsCount > 0 && (
        <>
          <H5 growX supplementary>
            {t('person.relationships.tombstoned_plural', 'Tombstoned ({count})', {
              count: tombstonedParentsRelationshipsCount,
            })}
          </H5>
          {isAuthorized('FtWritePermission') &&
            tombstonedParentsRelationshipsCount > 0 &&
            relationshipsToRestore.length > 0 && (
              <Button
                id="parentCollectiveRestore"
                emphasis="medium"
                onClick={() => handleRestoreSelected(relationshipsToRestore, 'parentchild')}
              >
                {t('action.restoreParents', 'Restore ({count}) Parents', {
                  count: relationshipsToRestore.length,
                })}
              </Button>
            )}
          <DmcResponsiveCheckboxTable
            id="parentRestoreTable"
            totalColumnCount={6}
            onSelectionChange={setRelationshipsToRestore}
            atSizeColumns={{
              default: {
                columnWidths: [2, 2, 2, 2, 2, 2],
                labels: [
                  t('father', 'Father'),
                  t('owner', 'Owner'),
                  t('mother', 'Mother'),
                  t('owner', 'Owner'),
                  t('person.relationships.relationshipid', 'Relationship ID'),
                  '',
                ],
              },
            }}
          >
            {tombstonedParentsRelationships.map((relationship) => {
              return (
                <ResponsiveTableRow
                  key={relationship.relationshipId}
                  name={relationship.relationshipId}
                  column1={
                    <PersonInfo
                      key={`${relationship.relationshipId}_parent1`}
                      person={relationship.parent1}
                      accessControls={accessControls}
                    />
                  }
                  column2={
                    <OwnerId
                      key={`${relationship.relationshipId}_parent1_ownerId`}
                      ownerInfo={relationship.parent1?.ownerInfo}
                    />
                  }
                  column3={
                    <PersonInfo
                      key={`${relationship.relationshipId}_parent2`}
                      person={relationship.parent2}
                      accessControls={accessControls}
                    />
                  }
                  column4={
                    <OwnerId
                      key={`${relationship.relationshipId}_parent2_ownerId`}
                      ownerInfo={relationship.parent2?.ownerInfo}
                    />
                  }
                  column5={
                    <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="parentchild" />
                  }
                  column6={
                    <span css={floatRight}>
                      <Button
                        id={`parentRestoreBtn_${relationship.relationshipId}`}
                        emphasis="medium"
                        onClick={() => onRestore(relationship.relationshipId, 'parentchild')}
                        disabled={!isAuthorized('FtWritePermission')}
                      >
                        {t('action.restore', 'Restore')}
                      </Button>
                    </span>
                  }
                />
              )
            })}
          </DmcResponsiveCheckboxTable>
        </>
      )}
      <AddRelationships relationshipType="parents" />
      {showSeparator() && <Separator size="xl" />}
    </div>
  )
}
